/**
 * MealOperationsContext - Provides meal manipulation functions
 *
 * This context provides all the functions for modifying meals,
 * keeping them separate from data to optimize re-renders.
 */

import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { usePermissions } from './PermissionsContext';

// Create the context
const MealOperationsContext = createContext({
    // Meal CRUD operations
    addMeal: () => {},
    addMeals: () => {},
    updateMeal: () => {},
    removeMeal: () => {},
    removeMeals: () => {},
    batchOperations: () => {},

    // Modal operations
    startAddMeal: () => {},
    startReplaceMeal: () => {},

    // Rescheduling operations
    rescheduleMeals: () => {},
    applyOverlapResolution: () => {},

    // Utilities
    getMealsByType: () => {},
    getMealsByOffsetAndType: () => {},
    getLeftoversForMeal: () => {},
    getMealSlotInfo: () => {},
    getRescheduleOverlaps: () => {},
    getRescheduleOverlapsV3: () => {},
    createLeftoverMeals: () => {},
    
    // Cleanup utilities
    cancelGlobalCleanup: () => {},
    restoreAllDeletedMeals: () => {},

    // State
    error: null,
    history: null,

    // Computed values
    mealCount: 0,
    hasUnsavedChanges: false,

    // Settings
    alwaysSelectFood: true,
});

// Custom hook for consuming meal operations
export const useMealOperations = () => {
    const context = useContext(MealOperationsContext);

    if (!context) {
        throw new Error('useMealOperations must be used within MealOperationsProvider');
    }

    return context;
};

// Provider component
export const MealOperationsProvider = ({
    children,
    mealManager,
    alwaysSelectFood = true,
}) => {
    const permissions = usePermissions();

    // Create permission-protected operation wrappers
    const createProtectedOperation = (operation, allowWhenInhibited = false) => {
        if (!operation) return undefined;

        return (...args) => {
            // Check permission - meal operations are blocked when inhibitSwap is true
            if (!allowWhenInhibited && permissions.inhibitSwap) {
                const error = new Error('Permission denied: meal modifications are disabled (inhibitSwap)');
                console.warn('Meal operation blocked:', { operation: operation.name, inhibitSwap: true, permissions });
                return { success: false, error };
            }

            // Execute operation
            return operation(...args);
        };
    };

    // Functions with permission validation
    const value = {
        // Meal CRUD operations - blocked when inhibitSwap is true
        addMeal: createProtectedOperation(mealManager?.addMeal),
        addMeals: createProtectedOperation(mealManager?.addMeals),
        updateMeal: createProtectedOperation(mealManager?.updateMeal),
        removeMeal: createProtectedOperation(mealManager?.removeMeal),
        removeMeals: createProtectedOperation(mealManager?.removeMeals),
        batchOperations: createProtectedOperation(mealManager?.batchOperations),

        // Modal operations - blocked when inhibitSwap is true
        startAddMeal: createProtectedOperation(mealManager?.startAddMeal),
        startReplaceMeal: createProtectedOperation(mealManager?.startReplaceMeal),

        // Rescheduling operations - blocked when inhibitSwap is true
        rescheduleMeals: createProtectedOperation(mealManager?.rescheduleMeals),
        applyOverlapResolution: createProtectedOperation(mealManager?.applyOverlapResolution),

        // Read-only utilities - no permission required
        getMealsByType: mealManager?.getMealsByType,
        getMealsByOffsetAndType: mealManager?.getMealsByOffsetAndType,
        getLeftoversForMeal: mealManager?.getLeftoversForMeal,
        getMealSlotInfo: mealManager?.getMealSlotInfo,
        getRescheduleOverlaps: mealManager?.getRescheduleOverlaps,
        getRescheduleOverlapsV3: mealManager?.getRescheduleOverlapsV3,
        createLeftoverMeals: mealManager?.createLeftoverMeals,
        
        // Cleanup utilities - no permission required (used for undo)
        cancelGlobalCleanup: mealManager?.cancelGlobalCleanup,
        restoreAllDeletedMeals: mealManager?.restoreAllDeletedMeals,

        // State - read-only
        error: mealManager?.error || null,
        history: mealManager?.history || null,

        // Computed values - read-only
        mealCount: mealManager?.mealCount || 0,
        hasUnsavedChanges: mealManager?.hasUnsavedChanges || false,

        // Settings
        alwaysSelectFood,
        
        // Expose permissions for UI components
        permissions,
    };

    return (
        <MealOperationsContext.Provider value={value}>
            {children}
        </MealOperationsContext.Provider>
    );
};

MealOperationsProvider.propTypes = {
    children: PropTypes.node.isRequired,
    mealManager: PropTypes.object,
    alwaysSelectFood: PropTypes.bool,
};

export default MealOperationsContext;
